/**
 * Hexagonal grid renderer
 * Requirements: 6.3, 6.4
 *
 * Uses pointy-top hexagons with "odd-r" offset coordinates (matching grid-navigator):
 * - Pointy vertices on top and bottom (angle pointing up)
 * - Odd rows (y % 2 === 1) are shifted right by half a hexagon width
 *
 * Pointy-top hexagon geometry:
 *     /\
 *    /  \
 *   |    |  <- height = 2 * radius
 *    \  /
 *     \/
 *   ^-- width = sqrt(3) * radius
 *
 * Grid spacing (for seamless tiling with odd-r):
 * - Horizontal step: sqrt(3) * radius (full width, hexagons touch side to side)
 * - Vertical step: 1.5 * radius (3/4 of height overlap)
 * - Odd row offset: sqrt(3)/2 * radius to the right
 */

import type { Position } from '@langton/shared';
import { BaseRenderer } from './base-renderer';

export class HexagonalRenderer extends BaseRenderer {
  /**
   * Hexagonal grid has 6 directions
   */
  getDirectionCount(): number {
    return 6;
  }

  /**
   * Use transparent background for proper tile overlap
   */
  protected usesTransparentBackground(): boolean {
    return true;
  }

  /**
   * Get hexagon radius from cellSize
   * cellSize is treated as the height of the hexagon (2 * radius)
   */
  private getRadius(cellSize: number): number {
    return cellSize / 2;
  }

  /**
   * Calculate pixel dimensions for the offscreen canvas
   * Must include full hexagons at edges
   */
  getOffscreenDimensions(
    gridWidth: number,
    gridHeight: number,
    cellSize: number
  ): { width: number; height: number } {
    const radius = this.getRadius(cellSize);
    const hexWidth = Math.sqrt(3) * radius;

    // Width: gridWidth columns * horizontal step + odd row offset + right edge
    const width = gridWidth * hexWidth + hexWidth / 2 + hexWidth / 2;

    // Height: gridHeight rows * vertical step + bottom half of last row
    const height = gridHeight * 1.5 * radius + 0.5 * radius;

    return { width: Math.ceil(width), height: Math.ceil(height) };
  }

  /**
   * Tile dimensions for seamless tiling
   * This is the exact period of the grid pattern
   */
  getTileDimensions(
    gridWidth: number,
    gridHeight: number,
    cellSize: number,
    cellGapX: number = 0,
    cellGapY: number = 0
  ): { width: number; height: number } {
    const radius = this.getRadius(cellSize);
    const hexWidth = Math.sqrt(3) * radius;

    // Horizontal period: gridWidth * hexWidth
    const tileWidth = gridWidth * hexWidth;

    // Vertical period: gridHeight * 1.5 * radius
    const tileHeight = gridHeight * 1.5 * radius;

    return {
      width: tileWidth + cellGapX,
      height: tileHeight + cellGapY,
    };
  }

  /**
   * Get angle for direction (pointy-top hexagon)
   * Directions match grid-navigator: 0=E, 1=SE, 2=SW, 3=W, 4=NW, 5=NE
   * 
   * For avatar rotation, we need the angle the ant is "facing" when moving in that direction.
   * The avatar image points "up" by default (negative Y direction, angle = 0).
   * So we need to rotate it to point in the movement direction.
   */
  getDirectionAngle(direction: number): number {
    // Direction angles for pointy-top hexagon
    // Avatar points "up" (0°), we rotate to face the edge
    // 0=E (right), 1=SE, 2=SW, 3=W (left), 4=NW, 5=NE
    const angles = [
      Math.PI / 2,           // 0: E - face right (90°)
      2 * Math.PI / 3,       // 1: SE - face lower-right (120°)
      Math.PI - Math.PI / 6, // 2: SW - face lower-left (150°)
      -Math.PI / 2,          // 3: W - face left (-90° = 270°)
      -2 * Math.PI / 3,      // 4: NW - face upper-left (-120°)
      -Math.PI / 6           // 5: NE - face upper-right (-30°)
    ];
    return angles[direction % 6];
  }

  /**
   * Calculate cell center position in canvas coordinates
   * Uses "odd-r" offset: odd rows (y % 2 === 1) shifted right
   *
   * This matches the grid-navigator coordinate system exactly
   */
  getCellPosition(x: number, y: number, cellSize: number): Position {
    const radius = this.getRadius(cellSize);
    const hexWidth = Math.sqrt(3) * radius;
    const isOddRow = y % 2 === 1;

    // Center X: column * horizontal step + odd row offset + half width (to get center)
    const centerX = x * hexWidth + hexWidth / 2 + (isOddRow ? hexWidth / 2 : 0);

    // Center Y: row * vertical step + radius (to get center)
    const centerY = y * 1.5 * radius + radius;

    // Return top-left corner for consistency with other renderers
    return {
      x: centerX - hexWidth / 2,
      y: centerY - radius,
    };
  }

  /**
   * Get the center of a cell (for avatar positioning)
   */
  private getCellCenter(x: number, y: number, cellSize: number): Position {
    const radius = this.getRadius(cellSize);
    const hexWidth = Math.sqrt(3) * radius;
    const isOddRow = y % 2 === 1;

    return {
      x: x * hexWidth + hexWidth / 2 + (isOddRow ? hexWidth / 2 : 0),
      y: y * 1.5 * radius + radius,
    };
  }

  /**
   * Draw a pointy-top hexagonal cell
   * Clears the area first to prevent antialiasing artifacts from previous colors
   */
  drawCell(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    cellSize: number
  ): void {
    const center = this.getCellCenter(x, y, cellSize);
    const radius = this.getRadius(cellSize);

    // Create hexagon path
    const createHexPath = (): void => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = -Math.PI / 2 + (Math.PI / 3) * i;
        const vx = center.x + radius * Math.cos(angle);
        const vy = center.y + radius * Math.sin(angle);

        if (i === 0) {
          ctx.moveTo(vx, vy);
        } else {
          ctx.lineTo(vx, vy);
        }
      }
      ctx.closePath();
    };

    // Clear the hexagon area first with a slightly larger clip to remove antialiasing artifacts
    ctx.save();
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = -Math.PI / 2 + (Math.PI / 3) * i;
      const vx = center.x + (radius + 1) * Math.cos(angle);
      const vy = center.y + (radius + 1) * Math.sin(angle);
      if (i === 0) {
        ctx.moveTo(vx, vy);
      } else {
        ctx.lineTo(vx, vy);
      }
    }
    ctx.closePath();
    ctx.clip();
    ctx.clearRect(
      center.x - radius - 2,
      center.y - radius - 2,
      radius * 2 + 4,
      radius * 2 + 4
    );
    ctx.restore();

    // Draw the hexagon with new color
    ctx.fillStyle = color;
    createHexPath();
    ctx.fill();
  }

  /**
   * Get hexagonal clipping path for avatar
   */
  getAvatarClipPath(
    _ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    size: number
  ): Path2D {
    const path = new Path2D();
    const radius = size / 2;

    // Pointy-top hexagon
    for (let i = 0; i < 6; i++) {
      const angle = -Math.PI / 2 + (Math.PI / 3) * i;
      const vx = centerX + radius * Math.cos(angle);
      const vy = centerY + radius * Math.sin(angle);

      if (i === 0) {
        path.moveTo(vx, vy);
      } else {
        path.lineTo(vx, vy);
      }
    }

    path.closePath();
    return path;
  }
}
